import { Navigate } from 'react-router-dom';
import { useAuthStore, type PmoRole } from '../../store/useAuthStore';

interface AuthGuardProps {
  children: React.ReactNode;
  roles?: PmoRole[];
  fallback?: React.ReactNode;
}

/**
 * Route guard that requires authentication and optionally specific roles.
 * If not authenticated, redirects to /login.
 * If authenticated but missing required role, shows fallback or 403.
 */
export default function AuthGuard({ children, roles, fallback }: AuthGuardProps) {
  const { user, loading, hasRole } = useAuthStore();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <div className="w-8 h-8 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles && roles.length > 0 && !hasRole(...roles)) {
    if (fallback) return <>{fallback}</>;

    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <span className="text-amber-600 text-lg">🔒</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Access Denied
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            You don't have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
